import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Search, ShoppingBag, ClipboardList, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCartStore, useAuthStore } from '@/store';
import { Badge } from '@/components/ui/badge';

const navItems = [
  { path: '/', icon: Home, label: 'Home' },
  { path: '/search', icon: Search, label: 'Search', action: 'search' },
  { path: '/cart', icon: ShoppingBag, label: 'Cart', action: 'cart' },
  { path: '/orders', icon: ClipboardList, label: 'Orders', auth: true },
  { path: '/profile', icon: User, label: 'Profile', auth: true, fallback: '/auth' },
];

export default function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { itemCount } = useCartStore();
  const { isAuthenticated } = useAuthStore();

  // Don't show on certain pages
  const hiddenPaths = ['/auth', '/checkout', '/merchant', '/kiosk', '/live-track'];
  const shouldHide = hiddenPaths.some(p => location.pathname.startsWith(p));
  
  if (shouldHide) return null;

  const handleNavClick = (item) => {
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }

    if (item.action === 'cart') {
      window.dispatchEvent(new Event('toggleCart'));
      return;
    }

    if (item.action === 'search') {
      // Focus on search input on home page
      navigate('/');
      setTimeout(() => {
        const searchInput = document.querySelector('[data-testid="search-input"]');
        if (searchInput) searchInput.focus();
      }, 100);
      return;
    }

    if (item.auth && !isAuthenticated) {
      navigate(item.fallback || '/auth');
      return;
    }

    navigate(item.path);
  };

  const isActive = (item) => {
    if (item.path === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.path);
  };

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          const showBadge = item.action === 'cart' && itemCount > 0;

          return (
            <button
              key={item.path}
              onClick={() => handleNavClick(item)}
              className={cn(
                "flex flex-col items-center justify-center w-16 h-14 rounded-xl transition-all duration-200",
                "active:scale-95 touch-manipulation",
                active 
                  ? "text-primary bg-primary/10" 
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              <div className="relative">
                <Icon className={cn("h-5 w-5", active && "text-primary")} />
                {showBadge && (
                  <Badge 
                    className="absolute -top-2 -right-2 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-primary"
                  >
                    {itemCount > 9 ? '9+' : itemCount}
                  </Badge>
                )}
              </div>
              <span className={cn(
                "text-[10px] mt-1 font-medium",
                active && "text-primary"
              )}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
